import { useState } from 'react';

import {
  CheckIndividualEmailResponse,
  individualsService,
  setCognitoContext,
  useToast,
} from '@aslanpay/portal-common-lib';
import { useNavigate } from 'react-router-dom';

import { AuthenticationStep } from 'app/components/Authentication/types';
import { EMPLOYEE_PORTAL_ORIGIN } from 'app/constants/authentication';
import { unauthenticatedPaths } from 'app/constants/routes';
import useSsoContextSwitch from 'app/hooks/useSsoContextSwitch';
import { useAuthenticationStore } from 'app/store';

// EmailEvent result codes - checkEmailStep already resolves status/auth-method into one of these.
const SIGN_IN_RESULT_CODE = 1;
const SIGN_UP_RESULT_CODE = 2;
const INVALID_EMAIL_RESULT_CODE = 3;
const SIGN_IN_USING_SSO_RESULT_CODE = 4;

// TODO(ASD-4408): portal-common-lib's CheckIndividualEmailResponse is missing phoneNumberRequired,
// even though the backend's CheckEmailResponse always sends it for an SSO identity - widen locally
// until that type is fixed upstream.
type CheckEmailResult = CheckIndividualEmailResponse & { phoneNumberRequired?: boolean };

/**
 * Resolves an employee-portal switch from ONE call - checkEmailStep already resolves status and
 * auth-method internally (SIGN_IN / SIGN_UP / SIGN_IN_USING_SSO / INVALID_EMAIL), so there's no need
 * to duplicate that resolution here. Sends a distinct origin so the backend resolves the employee
 * identity directly instead of the public check-email screen's admin-first default, which would
 * otherwise never disclose this dual user's employee role.
 *
 * Shared by the portal switcher menu (called directly, for the cases with no side effect) and the
 * employee-switch confirm screen (called from its "Verify email" button, for the INVITED case, which
 * sends a code as a side effect). When `onSetStep` is given, a SIGN_IN/SIGN_UP result moves within the
 * already-mounted Authentication page instead of navigating there.
 */
const useResolveEmployeeSwitch = (onSetStep?: (step: AuthenticationStep) => void) => {
  const navigate = useNavigate();
  const toast = useToast();
  const setAuthenticationData = useAuthenticationStore((state) => state.setAuthenticationData);
  const startSsoSwitch = useSsoContextSwitch();
  const [isResolving, setIsResolving] = useState(false);

  const resolveEmployeeSwitch = async (email: string) => {
    setIsResolving(true);

    try {
      const response: CheckEmailResult = await individualsService.checkIndividualEmail(
        { email },
        { origin: EMPLOYEE_PORTAL_ORIGIN }
      );

      switch (response.result) {
        case SIGN_IN_RESULT_CODE:
          setCognitoContext('EMPLOYEE');
          setAuthenticationData({ email, loginContext: 'EMPLOYEE', isContextSwitch: true });
          if (onSetStep) {
            onSetStep('login');
          } else {
            navigate(unauthenticatedPaths.authentication);
          }
          return;
        case SIGN_UP_RESULT_CODE:
          setCognitoContext('EMPLOYEE');
          setAuthenticationData({
            email,
            loginContext: 'EMPLOYEE',
            isContextSwitch: true,
            contextSwitchStep: 'verifyEmail',
            transactionId: response.transactionId,
          });
          if (onSetStep) {
            onSetStep('verifyEmail');
          } else {
            navigate(unauthenticatedPaths.authentication);
          }
          return;
        case SIGN_IN_USING_SSO_RESULT_CODE:
          await startSsoSwitch({
            context: 'EMPLOYEE',
            authenticationMethod: 'SSO_ENTRA',
            idpProviderName: response.idpProviderName,
            phoneNumberRequired: response.phoneNumberRequired === true,
          });
          return;
        case INVALID_EMAIL_RESULT_CODE:
        default:
          toast({
            position: 'top-right',
            status: 'info',
            title: 'Not invited yet',
            description:
              "This employee account hasn't been invited yet. Invite the employee first.",
          });
      }
    } catch {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not switch to the employee portal',
        description: 'Please try again, or contact support if the problem persists.',
      });
    } finally {
      setIsResolving(false);
    }
  };

  return { resolveEmployeeSwitch, isResolving };
};

export default useResolveEmployeeSwitch;
